import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Feature, LineString } from 'geojson';
import * as L from 'leaflet';
import { GeoJSON, latLng, Map, Polygon, tileLayer } from 'leaflet';
import 'Leaflet.MultiOptionsPolyline';
import moment from 'moment';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { delay, first, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  notify,
  Route,
  RouteLoader,
  RouteParams, TrailerIntervention
} from 'shared';
import { MapLayersControlToolbar } from '../../map/map-layers-control/map-layers-control.component';
import { TractorService } from '../../stores/tractor.service';


@Component({
  selector: 'app-fields-interventions-map',
  templateUrl: './fields-interventions-map.component.html',
  styleUrls: ['./fields-interventions-map.component.scss']
})
export class FieldsInterventionsMapComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  intervention: TrailerIntervention;

  @Input()
  hidden: boolean;

  @Input()
  closed: Observable<void>;

  private map: Map;
  private _mapSubject = new ReplaySubject<Map>(1);
  mapReady$ = this._mapSubject.asObservable();

  layers = [
    {
      name: 'Hybride',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
    {
      name: 'Satellite',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
  ];

  mapOptions = {
    layers: this.layers.map(l => l.layer),
    zoom: 7,
    minZoom: 0,
    maxZoom: 21,
    maxNativeZoom: 21,
    preferCanvas: false,
    center: [33.626756199999996, -7.4964748],
  };

  private workingMultiPolygon: GeoJSON;
  private fieldPolygon: Polygon<any>;

  route?: Route;
  routeParams: RouteParams;
  routeLoader: RouteLoader = new RouteLoader();
  isRouteLoading: boolean;
  routeLoaded: boolean;
  private closedSub: Subscription;
  private routeLayer: any;
  private routePathLayer: any;
  private routeSegmentsPathLayer: any | null;
  private timeDimension: any;
  private timeDimensionPlayer: any;
  private timeDimensionControl: any;
  public mode: string = "speed";
  public legendUnit: string = "km/h"
  public legendMax: number;
  public legendMin: number;

  constructor(
    private tractorService: TractorService,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.closedSub = this.closed.subscribe(_ => {
      this.routeLoaded = false;
      if (this.timeDimensionControl) {
        this.map.removeControl(this.timeDimensionControl);
        this.timeDimensionPlayer.stop();
      }

      if (this.route) {
        if (this.routeLayer) {
          this.routeLayer.remove();
        }
        if (this.routeSegmentsPathLayer) {
          this.routeSegmentsPathLayer.remove();
          this.routeSegmentsPathLayer = null;
        }
        if (this.routePathLayer) {
          this.routePathLayer.remove();
        }
        // this.routeRenderer.reset();
        this.route = null;
      }
    });
  }

  onMapReady(map: Map) {
    this.map = map;
    this._mapSubject.next(map);
    L.control.scale({ imperial: false }).addTo(map);
    this.updateLegend();
  }

  loadRoute() {
    if (this.routeLoaded) {
      return;
    }
    
    // Check if this is a manual intervention - manual interventions don't have GPS route data
    const isManual = this.intervention.is_manual === true || this.intervention.intervention_source === 'manual';
    
    this.mapReady$.pipe(first(), delay(30)).subscribe(() => {

      // Clean the map
      this.cleanMap();

      // Add field to map
      if (this.intervention.field) {
        this.addFieldToMap();

      }

      // Add working multipolygon
      this.addWorkingMultipolygonToMap();
    });

    // For manual interventions, skip route loading and just show the working area
    if (isManual) {
      notify('Les interventions manuelles n\'ont pas de données de trajet GPS', 'info', 3000);
      this.routeLoaded = true;
      return;
    }

    // this.isRouteLoading = true;
    this.routeParams = new RouteParams(1);

    this.tractorService.route(this.routeLoader,
      this.intervention.vehicle_id,
      this.intervention.start_time,
      this.intervention.stop_time,
      this.routeParams,
    ).pipe(
      catchError((error) => {
        console.error('Error loading route:', error);
        notify('Impossible de charger l\'historique GPS pour cette intervention', 'error', 3000);
        this.routeLoaded = true;
        return of(null);
      })
    ).subscribe((r) => {
      if (!r) {
        return;
      }
      
      if (r.isLoaded) {
        // this.isRouteLoading = false;

        if (r.isEmpty()) {
          notify('Pas données dans l\'intervale sélectioné');
          return;
        }
        this.route = r.route;

        const icon = L.icon({
          iconUrl: '/assets/icons/circle-marker-green.png',
          iconSize: [16, 16],
          iconAnchor: [8, 8]
        });

        if (this.intervention.hasOperationsRules()) {

          if (this.mode == "pressure") {
            let lp = this.intervention.trailer_operation_type_rules.min_pressure,
              up = this.intervention.trailer_operation_type_rules.max_pressure,
              lv = this.intervention.trailer_operation_type_rules.min_voltage,
              uv = this.intervention.trailer_operation_type_rules.max_voltage;
            this.routeSegmentsPathLayer = L.multiOptionsPolyline(
              this.route.data.map(r => latLng(r.lat, r.lng, ((r.adc5 - lv) * (up - lp) / (uv - lv) + lp) * 10)), {
              multiOptions: {
                optionIdxFn: (latLng) => {
                  var i,
                    altThresholds = [
                      this.intervention.trailer_operation_type_rules.min_pressure,
                      this.intervention.trailer_operation_type_rules.max_pressure,
                    ];

                  for (i = 0; i < altThresholds.length; ++i) {
                    if (latLng.alt <= altThresholds[i]) {
                      return i;
                    }
                  }

                  return altThresholds.length;
                },
                options: [
                  { color: '#0080FF', weight: 5, lineCap: 'butt', smoothFactor: 1 },
                  { color: '#00ff00', weight: 5, lineCap: 'butt', smoothFactor: 1 },
                  { color: '#ff0000', weight: 5, lineCap: 'butt', smoothFactor: 1 },
                ]
              }
            },
            );
          } else {
            this.routeSegmentsPathLayer = L.multiOptionsPolyline(
              this.route.data.map(r => latLng(r.lat, r.lng, r.speed)), {
              multiOptions: {
                optionIdxFn: (latLng) => {
                  var i,
                    altThresholds = [
                      this.intervention.trailer_operation_type_rules.min_speed,
                      this.intervention.trailer_operation_type_rules.max_speed,
                    ];

                  for (i = 0; i < altThresholds.length; ++i) {
                    if (latLng.alt <= altThresholds[i]) {
                      return i;
                    }
                  }

                  return altThresholds.length;
                },
                options: [
                  { color: '#0080FF', weight: 5, lineCap: 'butt', smoothFactor: 1 },
                  { color: '#00ff00', weight: 5, lineCap: 'butt', smoothFactor: 1 },
                  { color: '#ff0000', weight: 5, lineCap: 'butt', smoothFactor: 1 },
                ]
              }
            },
            );
          }
        } else {
          this.routeSegmentsPathLayer = null;
        }

        this.routePathLayer = L.geoJSON({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: this.route.data.map(p => p.coords())
          },
          properties: {
            coordTimes: this.route.data.map(p => moment(p.tracker_time).format('YYYY-MM-DD HH:mm:ss'))
          }
        } as Feature<LineString>, {
          pointToLayer: (feature, latLng) => {
            if (feature.properties.hasOwnProperty('last')) {
              return new L.Marker(latLng, {
                icon
              });
            }
            return L.circleMarker(latLng);
          }
        });

        this.mapReady$.pipe(first(), delay(30)).subscribe(() => {

          this.newTimeDimension();

          this.routeLayer = L.timeDimension.layer.geoJson(this.routePathLayer, {
            updateTimeDimension: true,
            duration: 'PT2M',
            updateTimeDimensionMode: 'replace',
            addlastPoint: true
          });

          this.routePathLayer.addTo(this.map);
          if (this.routeSegmentsPathLayer) {
            this.routeSegmentsPathLayer.addTo(this.map);
          }
          this.routeLayer.addTo(this.map);
        });
        // this.routeRenderer.render(this.map, this.route, this.routeParams);
      }
      this.routeLoaded = true;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('hidden')) {
      if (!changes.hidden.currentValue && !this.route) {
        this.loadRoute();
      }
    }
  }

  ngOnDestroy(): void {
    this.closedSub.unsubscribe();
  }

  private newTimeDimension() {

    this.timeDimension = new L.timeDimension({
      period: 'PT1S',
    });

    // @ts-ignore
    this.map.timeDimension = this.timeDimension;

    // @ts-ignore
    this.timeDimensionPlayer = new L.TimeDimension.Player({
      transitionTime: 1000,
      loop: true
    }, this.timeDimension);

    // @ts-ignore
    this.timeDimensionControl = new L.Control.TimeDimension({
      player: this.timeDimensionPlayer,
      timeDimension: this.timeDimension,
      timeSliderDragUpdate: true,
      loopButton: true,
      autoPlay: false,
      position: 'bottomleft',
    });
    this.map.addControl(this.timeDimensionControl);
  }


  addFieldToMap() {
    const f = this.intervention.field;
    this.fieldPolygon = f.createLayer({
      weight: 2,
      opacity: 0.4,
      fillOpacity: 0.3,
      color: f.color,
    });

    this.map.addLayer(this.fieldPolygon);

    this.map.fitBounds(this.fieldPolygon.getBounds(), {
      padding: [15, 15],
      maxZoom: 22,
    });
  }

  updateLegend() {
    if (this.mode == "pressure") {
      this.legendUnit = "Bar"

      this.legendMin = this.intervention.trailer_operation_type_rules.min_pressure || 0;
      this.legendMax = this.intervention.trailer_operation_type_rules.max_pressure || 0;
    } else {
      this.legendUnit = "km/h"
      this.legendMin = this.intervention.trailer_operation_type_rules.min_speed || 0;
      this.legendMax = this.intervention.trailer_operation_type_rules.max_speed || 0;
    }
    this.cdr.detectChanges();
  }

  addWorkingMultipolygonToMap() {
    // Check if intervention has toGeoJSON method (TrailerIntervention instance)
    // or if we need to use working_multipolygon_geojson directly
    let geoJsonData: any = null;
    
    if (this.intervention && typeof this.intervention.toGeoJSON === 'function') {
      geoJsonData = this.intervention.toGeoJSON();
    } else if (this.intervention && (this.intervention as any).working_multipolygon_geojson) {
      // Use the geojson directly if available
      geoJsonData = new GeoJSON((this.intervention as any).working_multipolygon_geojson);
    } else if (this.intervention && (this.intervention as any).working_multipolygon) {
      // Fallback to working_multipolygon if available
      geoJsonData = new GeoJSON((this.intervention as any).working_multipolygon);
    }
    
    if (!geoJsonData) {
      return;
    }
    
    this.workingMultiPolygon = geoJsonData;
    this.workingMultiPolygon.setStyle({
      fillColor: '#4ef34a',
      fill: true,
      weight: 1,
      interactive: true,
      fillOpacity: 0.6,
      color: '#04b800',
      stroke: true,
    });
    this.workingMultiPolygon.addTo(this.map);
  }

  removeFieldFromMap() {
    if (this.fieldPolygon) {
      this.fieldPolygon.remove();
    }
  }

  removeWorkingMultipolygonFromMap() {
    if (this.workingMultiPolygon) {
      this.workingMultiPolygon.remove();
    }
  }

  cleanMap() {
    this.removeFieldFromMap();
    this.removeWorkingMultipolygonFromMap();
  }

  mapLayersControlLoaded(items: MapLayersControlToolbar) {
    items.add('viewMode', {
      location: 'before',
      locateInMenu: 'never',
      widget: 'dxDropDownButton',
      options: {
        items: [
          {
            id: 'speed',
            name: 'vitesse',
          },
          {
            id: 'pressure',
            name: 'pression',
          }
        ],
        keyExpr: 'id',
        displayExpr: 'name',
        selectedItemKey: 'speed',
        stylingMode: "text",
        useSelectMode: true,
        width: 100,
        onItemClick: (e: any) => {
          this.mode = e.itemData.id
          this.routeLoaded = false;
          this.map.removeControl(this.timeDimensionControl);
          this.updateLegend()
          this.cleanMap()
          this.loadRoute()
        }
      }
    });
  }
}
